<!-- Keyboard shortcut badge rendering OS-aware key symbols and labels. -->

<script lang="ts">
import { getOS } from '@nzyme/dom-utils/getOs.js';
import { ArrowBigUp, ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Command, CornerDownLeft, Space } from 'lucide-vue-next';
import { computed } from 'vue';
import type { Component } from 'vue';

import type { Shortcut } from '@sigma/client/utils/Shortcut.js';

export type ShortcutColor = 'black' | 'blue' | 'gray' | 'green' | 'red' | 'white';
export type ShortcutSize = 'small';

const displayMap: Record<string, string | Component | undefined> = {
  ' ': Space, // Space
  Space: Space, // Space (alternative)
  Enter: CornerDownLeft, // Enter/Return
  Escape: 'Esc', // Escape
  ArrowUp: ArrowUp, // Up arrow
  ArrowDown: ArrowDown, // Down arrow
  ArrowLeft: ArrowLeft, // Left arrow
  ArrowRight: ArrowRight, // Right arrow
};
</script>

<script lang="ts" setup>
interface ShortcutProps {
  shortcut?: Shortcut | null;
  color?: ShortcutColor | null;
  size?: ShortcutSize | null;
}

const props = defineProps<ShortcutProps>();
const os = getOS();

const display = computed(() => {
  const key = props.shortcut?.key;
  if (!key) {
    return null;
  }

  return displayMap[key] || key.toUpperCase();
});
</script>

<template>
  <kbd
    v-if="shortcut"
    :class="[css.shortcut, color && css[`color_${color}`], size && css[`size_${size}`]]"
  >
    <template v-if="shortcut?.cmd">
      <Command
        v-if="os === 'macOS'"
        :class="css.icon"
      />
      <span v-else>Ctrl</span>
    </template>

    <template v-if="shortcut?.ctrl">
      <span>Ctrl</span>
    </template>

    <template v-if="shortcut?.shift">
      <ArrowBigUp :class="css.icon" />
    </template>

    <span v-if="typeof display === 'string'">{{ display }}</span>
    <component
      :is="display"
      v-else
      :class="css.icon"
    />
  </kbd>
</template>

<style lang="scss" module="css">
.shortcut {
  display: inline-flex;
  align-items: center;
  gap: $spacing-04;
  background: $color-gray-50;
  color: $color-gray-600;
  border: 1px solid $color-gray-100;
  border-radius: $border-radius-04;
  padding: $spacing-04 $spacing-08;
  font-size: 0.875em; // 14px equivalent
  font-weight: 500;
  line-height: 1; // 24px equivalent at 14px base
  font-family: inherit;
}

// Colors match the button accents they sit on (the app's "blue" is the doctor
// teal): a darker shade of the accent as the chip fill with a lighter accent
// border, echoing the button's outline treatment.
.color {
  &_blue {
    background: $color-doctor-800;
    color: $color-white;
    border-color: $color-doctor-500;
  }

  &_red {
    background: $color-red-700;
    color: $color-white;
    border-color: $color-red-400;
  }

  &_green {
    background: $color-green-600;
    color: $color-white;
    border-color: $color-green-400;
  }

  &_black {
    background: $color-gray-900;
    color: $color-white;
    border-color: $color-gray-600;
  }

  &_gray {
    background: $color-gray-700;
    color: $color-white;
    border-color: $color-gray-400;
  }

  &_white {
    background: $color-white;
    color: $color-gray-700;
    border-color: $color-gray-200;
  }
}

.size {
  &_small {
    font-size: $font-size-12;
  }
}

.icon {
  width: 1em;
  height: 1em;
}
</style>
